import React, {useEffect, useState} from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Slider,
  ThemeProvider,
  Tabs,
  Tab,
  Divider,
  createMuiTheme,
} from "@material-ui/core";
import {
  CloudUpload as UploadIcon,
  Send as SendIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as PreviewIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  Image as ImageIcon,
} from "@material-ui/icons";
import ImportParticipantsDialog from "./ImportParticipantsDialog";
import CertificatePreview from "./CertificatePreview";
import ParticipantEditDialog from "./ParticipantEditDialog";
import AdminLayout from "../../../core/AdminLayout";
import {ToastContainer, toast} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

const theme = createMuiTheme({
  palette: {
    primary: {
      main: "#2d3748",
      light: "#4a5568",
      dark: "#1a202c",
    },
    secondary: {
      main: "#f59e0b",
      light: "#fbbf24",
      dark: "#d97706",
    },
    success: {
      main: "#10b981",
    },
    error: {
      main: "#ef4444",
    },
  },
  typography: {
    fontFamily: '"IBM Plex Sans", "Helvetica Neue", Arial, sans-serif',
  },
  shape: {
    borderRadius: 12,
  },
});

function TabPanel({children, value, index}) {
  return (
    <div hidden={value !== index} style={{paddingTop: 24}}>
      {value === index && children}
    </div>
  );
}

export default function WorkshopDetails({match}) {
  const [workshop, setWorkshop] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [certificateSettings, setCertificateSettings] = useState({
    name_x: 600,
    name_y: 400,
    font_size: 48,
    font_color: "#000000",
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const API_URL = window?.location?.hostname?.includes("maroc")
    ? process.env.REACT_APP_API_MAROC
    : process.env.REACT_APP_API;

  const fetchWorkshop = () => {
    axios
      .get(`${API_URL}/workshops/${match.params.id}`)
      .then((res) => {
        setWorkshop(res.data);
        setCertificateSettings({
          name_x: res.data.name_x || 2250,
          name_y: res.data.name_y || 2150,
          font_size: res.data.font_size || 64,
          font_color: res.data.font_color || "#000000",
        });
      })
      .catch(() => toast.error("Failed to load workshop"));
  };

  useEffect(() => {
    fetchWorkshop();
  }, []);

  const handleDeleteParticipant = async (participantId) => {
    if (window.confirm("Are you sure you want to delete this participant?")) {
      try {
        await axios.delete(`${API_URL}/workshops/${workshop.id}/participants/${participantId}`);
        toast.success("Participant deleted successfully");
        fetchWorkshop();
      } catch (err) {
        toast.error("Failed to delete participant");
      }
    }
  };

  const handleEditParticipant = (participant) => {
    setSelectedParticipant(participant);
    setEditOpen(true);
  };

  const handleAddParticipant = () => {
    setSelectedParticipant(null);
    setEditOpen(true);
  };

  const handleUpdateSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API_URL}/workshops/${workshop.id}/settings`, certificateSettings);
      toast.success("Certificate settings saved successfully");
      fetchWorkshop(); // Refresh to get updated data
    } catch (err) {
      console.error("Error updating settings:", err);
      toast.error("Failed to update settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleGenerateCertificates = async () => {
    if (!workshop.participants || workshop.participants.length === 0) {
      toast.warning("No participants to generate certificates for");
      return;
    }

    // Check if settings are configured
    if (!workshop.name_x || !workshop.name_y) {
      toast.error("Please configure certificate settings before generating certificates");
      setTabValue(1); // Switch to settings tab
      return;
    }

    if (
      window.confirm(
        `Generate and send certificates to ${workshop.participants.length} participants?\n\nCurrent settings:\nX: ${workshop.name_x}px\nY: ${workshop.name_y}px\nFont Size: ${workshop.font_size}px`,
      )
    ) {
      try {
        const response = await axios.post(`${API_URL}/workshops/${workshop.id}/generate-certificates`);
        toast.success(
          `Certificate generation started for ${response.data.count} participants. Check your queue worker for progress.`,
        );

        // Refresh workshop data after a short delay
        setTimeout(() => {
          fetchWorkshop();
        }, 2000);
      } catch (err) {
        console.error("Error generating certificates:", err);
        toast.error(err.response?.data?.message || "Failed to generate certificates");
      }
    }
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    fetchWorkshop(); // Refresh workshop data when preview closes (in case settings were saved)
  };

  if (!workshop) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <Typography>Loading workshop...</Typography>
        </Box>
      </AdminLayout>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <AdminLayout>
        <ToastContainer position="top-right" autoClose={3000} />

        <Box mb={4}>
          {/* Header */}
          <Paper elevation={2} style={{padding: 32, marginBottom: 24, borderRadius: 12}}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h4" gutterBottom style={{fontWeight: 700}}>
                  {workshop.title}
                </Typography>
                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                  <Chip icon={<ImageIcon />} label={workshop.date} color="primary" />
                  <Chip
                    icon={<PeopleIcon />}
                    label={`${workshop.participants?.length || 0} Participants`}
                    color="secondary"
                  />
                  <Chip
                    icon={<SettingsIcon />}
                    label={`Position: ${workshop.name_x}x${workshop.name_y}`}
                    variant="outlined"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    startIcon={<PreviewIcon />}
                    onClick={() => setPreviewOpen(true)}
                    style={{textTransform: "none"}}
                  >
                    Preview & Adjust Settings
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    fullWidth
                    startIcon={<SendIcon />}
                    onClick={handleGenerateCertificates}
                    style={{textTransform: "none", fontWeight: 600}}
                  >
                    Generate & Send Certificates
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Tabs */}
          <Paper elevation={0} style={{borderRadius: 12}}>
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
              indicatorColor="secondary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab label="Participants" icon={<PeopleIcon />} style={{textTransform: "none"}} />
              <Tab label="Certificate Settings" icon={<SettingsIcon />} style={{textTransform: "none"}} />
            </Tabs>
          </Paper>

          {/* Participants Tab */}
          <TabPanel value={tabValue} index={0}>
            <Paper elevation={2} style={{padding: 24, borderRadius: 12}}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" style={{fontWeight: 600}}>
                  Participant List
                </Typography>
                <Box display="flex" gap={2}>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddParticipant}
                    style={{textTransform: "none"}}
                  >
                    Add Participant
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<UploadIcon />}
                    onClick={() => setImportOpen(true)}
                    style={{textTransform: "none"}}
                  >
                    Import from Excel
                  </Button>
                </Box>
              </Box>

              {workshop.participants && workshop.participants.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow style={{backgroundColor: "#f7fafc"}}>
                        <TableCell style={{fontWeight: 600}}>Prefix</TableCell>
                        <TableCell style={{fontWeight: 600}}>First Name</TableCell>
                        <TableCell style={{fontWeight: 600}}>Last Name</TableCell>
                        <TableCell style={{fontWeight: 600}}>School</TableCell>
                        <TableCell style={{fontWeight: 600}}>Email</TableCell>
                        <TableCell style={{fontWeight: 600}}>Phone</TableCell>
                        <TableCell style={{fontWeight: 600}}>Status</TableCell>
                        <TableCell align="right" style={{fontWeight: 600}}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {workshop.participants.map((participant) => (
                        <TableRow key={participant.id} hover style={{cursor: "pointer"}}>
                          <TableCell>{participant.prefix || "-"}</TableCell>
                          <TableCell style={{fontWeight: 500}}>{participant.fname}</TableCell>
                          <TableCell style={{fontWeight: 500}}>{participant.lname}</TableCell>
                          <TableCell>{participant.school || "-"}</TableCell>
                          <TableCell>{participant.email}</TableCell>
                          <TableCell>{participant.tel || "-"}</TableCell>
                          <TableCell>
                            <Chip
                              label={participant.sent ? "Sent" : "Pending"}
                              color={participant.sent ? "primary" : "default"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleEditParticipant(participant)}
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteParticipant(participant.id)}
                              style={{color: "#ef4444"}}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box textAlign="center" py={8}>
                  <PeopleIcon style={{fontSize: 64, color: "#cbd5e0", marginBottom: 16}} />
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    No participants yet
                  </Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Add participants manually or import them from an Excel file
                  </Typography>
                  <Box display="flex" gap={2} justifyContent="center">
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={handleAddParticipant}
                    >
                      Add Participant
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<UploadIcon />}
                      onClick={() => setImportOpen(true)}
                    >
                      Import from Excel
                    </Button>
                  </Box>
                </Box>
              )}
            </Paper>
          </TabPanel>

          {/* Certificate Settings Tab */}
          <TabPanel value={tabValue} index={1}>
            <Paper elevation={2} style={{padding: 24, borderRadius: 12}}>
              <Typography variant="h6" gutterBottom style={{fontWeight: 600}}>
                Certificate Template Settings
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Adjust the position and appearance of the participant name on the certificate
              </Typography>

              <Divider style={{margin: "24px 0"}} />

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography gutterBottom style={{fontWeight: 500}}>
                    Name Position X: {certificateSettings.name_x}px
                  </Typography>
                  <Slider
                    value={certificateSettings.name_x}
                    onChange={(e, val) => setCertificateSettings({...certificateSettings, name_x: val})}
                    min={0}
                    max={3000}
                    step={10}
                    valueLabelDisplay="auto"
                    color="secondary"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography gutterBottom style={{fontWeight: 500}}>
                    Name Position Y: {certificateSettings.name_y}px
                  </Typography>
                  <Slider
                    value={certificateSettings.name_y}
                    onChange={(e, val) => setCertificateSettings({...certificateSettings, name_y: val})}
                    min={0}
                    max={3000}
                    step={10}
                    valueLabelDisplay="auto"
                    color="secondary"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography gutterBottom style={{fontWeight: 500}}>
                    Font Size: {certificateSettings.font_size}px
                  </Typography>
                  <Slider
                    value={certificateSettings.font_size}
                    onChange={(e, val) => setCertificateSettings({...certificateSettings, font_size: val})}
                    min={12}
                    max={120}
                    step={2}
                    valueLabelDisplay="auto"
                    color="secondary"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography gutterBottom style={{fontWeight: 500}}>
                    Font Color
                  </Typography>
                  <TextField
                    type="color"
                    fullWidth
                    value={certificateSettings.font_color}
                    onChange={(e) =>
                      setCertificateSettings({
                        ...certificateSettings,
                        font_color: e.target.value,
                      })
                    }
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<PreviewIcon />}
                      onClick={() => setPreviewOpen(true)}
                      style={{textTransform: "none"}}
                    >
                      Preview Changes
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleUpdateSettings}
                      disabled={savingSettings}
                      style={{textTransform: "none", fontWeight: 600}}
                    >
                      {savingSettings ? "Saving..." : "Save Settings"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </TabPanel>
        </Box>

        {/* Dialogs */}
        <ImportParticipantsDialog
          open={importOpen}
          onClose={() => {
            setImportOpen(false);
            fetchWorkshop();
          }}
          workshopId={workshop.id}
        />

        <CertificatePreview
          open={previewOpen}
          onClose={handlePreviewClose}
          workshop={workshop}
          settings={certificateSettings}
        />

        <ParticipantEditDialog
          open={editOpen}
          onClose={() => {
            setEditOpen(false);
            fetchWorkshop();
          }}
          participant={selectedParticipant}
          workshopId={workshop.id}
        />
      </AdminLayout>
    </ThemeProvider>
  );
}
